import {
  useState,
  useId,
  useRef,
  useLayoutEffect,
  isValidElement,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type MutableRefObject,
  type MouseEventHandler,
  ReactNode,
} from "react";
import cx from "classnames";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";

import { Button } from "../../Button";
import { Chip, ChipProps } from "../../Chip";
import { Flex } from "../../Flex";
import { FieldLabel, FieldLabelProps } from "../../FieldLabel";
import { Helper } from "../../../internal/components";
import { warnDeprecatedErrorUsage } from "../../FieldMessage";
import { TextFieldProps } from "../../TextField";

import styles from "./SelectTriggerBase.module.scss";
import { DistributiveOmit } from "../../../types";
import { defaultItemToString } from "../../Combobox/internal/ComboboxUtils";

type PartialChipProps = Partial<Omit<ChipProps, "icon" | "avatar">> &
  (
    | { icon?: ChipProps["icon"]; avatar?: never }
    | { icon?: never; avatar?: ChipProps["avatar"] }
  );

function mergeChipProps(
  ...propSets: (PartialChipProps | Partial<ChipProps>)[]
): ChipProps {
  const merged = propSets.reduce(
    (acc, props) => ({ ...acc, ...props }),
    {} as Record<string, unknown>,
  );

  return merged as ChipProps;
}

export type SelectTriggerBaseProps<Item = unknown> = DistributiveOmit<
  TextFieldProps,
  "showCounter" | "prefix"
> & {
  /**
   * Should be a positive integer.
   */
  maxRows?: number;

  /** Allows Combobox/Select to display custom-colored Chips */
  selectedItemProps?: (item: Item, index: number) => PartialChipProps;
} & {
  itemToString?: (item: Item) => string;
  removeSelectedItem?: (item: Item) => void;
  disableClearSelection?: boolean;
  selectedItem: Item | null;
  selectedItems: Item[];

  inputValue?: string;
  referenceRef?: MutableRefObject<HTMLDivElement | null>;
} & {
  variant?: "select" | "combobox";
  onClearButtonClick?: MouseEventHandler<HTMLButtonElement>;
  labelProps?: Partial<FieldLabelProps>;
  inputWrapperProps?: ComponentPropsWithoutRef<"div">;
  toggleButtonProps?: ComponentPropsWithoutRef<"button">;
  chipProps?: (item: Item, index: number) => PartialChipProps;
  inputProps?: ComponentPropsWithoutRef<"input" | "div">;
} & {
  prefix?: string | ReactNode;
};

export const SelectTriggerBase = function <Item>({
  className,
  label,
  size,
  error,
  hint,
  description,
  prefix,
  suffix,
  maxRows,
  selectedItemProps = (_item: Item, _index: number): PartialChipProps => ({}),
  disabled,
  readOnly,
  /** ======== */
  disableClearSelection,
  itemToString: itemToStringProp,
  selectedItem,
  selectedItems,
  removeSelectedItem,
  inputValue,
  referenceRef,
  /** ======== */
  variant = "select",
  onClearButtonClick,
  labelProps,
  inputWrapperProps = {},
  toggleButtonProps = {},
  inputProps = {},
  chipProps = (_item: Item, _index: number): PartialChipProps => ({}),
  warning,
  placeholder,
  ...rest
}: SelectTriggerBaseProps<Item>) {
  const itemToString = itemToStringProp ?? defaultItemToString<Item>;

  const ComboboxTriggerClassNames = cx(styles["search-field"], {
    [styles["select"]]: variant === "select",
  });

  const noClearButton = disabled || readOnly || disableClearSelection;

  const ComboboxInputWrapperClassNames = cx(
    styles["input-wrapper"],
    className
      ? {
          [className]: variant === "select",
        }
      : {},
    {
      [styles["small"]]: size === "small",
      [styles["large"]]: size === "large",
      [styles["no-clear-button"]]: noClearButton,
    },
  );

  const ComboboxInputClassNames = cx(
    styles["input"],
    className
      ? {
          [className]: variant === "combobox",
        }
      : {},
    {
      [styles["error"]]: error,
    },
  );

  const helperUid = "helper" + useId();
  const placeholderUid = "placeholder" + useId();
  const ariaDescribedBy = `${helperUid} ${placeholderUid}`;

  warnDeprecatedErrorUsage("SelectTrigger", error);

  const errorMessages =
    typeof error === "boolean" || error === undefined ? undefined : error;

  const rowsRef = useRef<HTMLDivElement>(null);
  const [stillCalculating, setStillCalculating] = useState<boolean>(false);
  const [collapsedChips, setCollapsedChips] = useState<boolean>(false);
  const [visibleChipsCount, setVisibleChipsCount] = useState<number | null>(
    null,
  );
  const [forceRenderCount, setForceRenderCount] = useState<number>(0);

  useLayoutEffect(() => {
    // we only need to do this nonsense if maxRows is set
    if (maxRows === undefined) return;

    // 1) Render with no chips collapsed, if it's already just one row, we're done
    // 2) If there is a second row, then render again with best guess at how we should collapse
    // 3) If there is still a second row while collapsed, then render again with one less item
    // 4) Keep going until there is only one row, then /actually/ render

    // start calculation loop, set intiial values and force re-render
    if (stillCalculating === false) {
      setStillCalculating(true);
      setCollapsedChips(false);
      setVisibleChipsCount(null);
      setForceRenderCount((x) => x + 1);

      // otherwise, calculate the number of rows
    } else if (rowsRef?.current?.children) {
      const children = rowsRef?.current?.children;

      // Exclude the input-flex element from row counting so the algorithm
      // only considers chip elements when deciding whether to collapse.
      // The input will naturally fit alongside chips thanks to its
      // reduced min-width.
      const chipChildren = Array.from(children).filter(
        (child) =>
          !(child as HTMLElement).classList?.contains(styles["input-flex"]),
      );

      const rowData = chipChildren.reduce(
        (acc, child) => {
          const top = child.getBoundingClientRect().top;

          if (!acc.length) {
            return [{ count: 1, top }];
          } else if (acc[acc.length - 1].top === top) {
            return [
              ...acc.slice(0, -1),
              { count: acc[acc.length - 1].count + 1, top },
            ];
          } else if (acc[acc.length - 1].top !== top) {
            return [...acc, { count: 1, top }];
          } else {
            return acc;
          }
        },
        [] as { count: number; top: number }[],
      );

      // Call it a day if:
      // We don't have a second row OR
      // We're already hiding everything OR
      // There's only one item selected
      if (
        !rowData[maxRows] ||
        visibleChipsCount === 0 ||
        selectedItems.length === 1
      ) {
        setStillCalculating(false);
      } else {
        setCollapsedChips(true);

        // otherwise, either guess how many visible chips we have
        if (visibleChipsCount == null) {
          if (rowData[maxRows]) {
            const quickGuess = Math.max(
              rowData
                .slice(0, maxRows)
                .reduce((sum, row) => sum + row.count, -1),
              0,
            );

            setVisibleChipsCount(quickGuess);
          } else {
            const naiveGuess = Math.max(
              rowData.reduce((sum, row) => sum + row.count, -2),
              0,
            );
            setVisibleChipsCount(naiveGuess);
          }

          // or decrease our last guess by 1 and try again
        } else {
          setVisibleChipsCount(visibleChipsCount - 1);
        }

        setForceRenderCount((x) => x + 1);
      }
    }
  }, [selectedItems.length, maxRows, forceRenderCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedItemsDisplayCount =
    collapsedChips && maxRows != null && visibleChipsCount !== null
      ? visibleChipsCount
      : selectedItems.length;

  // TODO: Update this to use a TextField component once we can pass props
  //       directly to both the label and the text input
  return (
    <div {...rest} className={ComboboxTriggerClassNames} ref={referenceRef}>
      {label ? (
        <FieldLabel
          {...labelProps}
          className={cx(styles["label"], labelProps?.className)}
        >
          {label}
        </FieldLabel>
      ) : null}

      <div
        {...inputWrapperProps}
        className={ComboboxInputWrapperClassNames}
        {...(variant === "select"
          ? {
              "aria-describedby": ariaDescribedBy,
              "aria-labelledby": labelProps?.id,
            }
          : {})}
      >
        <div className={styles["buttons-wrapper"]}>
          {(inputValue || selectedItem || selectedItems.length) &&
          !noClearButton ? (
            <div className={styles["close-button-wrapper"]}>
              <Button
                aria-label="clear selection"
                appearance="ghost"
                size="small"
                icon={Close}
                className={styles["close-button"]}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onClearButtonClick?.(e);
                }}
                tabIndex={-1}
              />
            </div>
          ) : null}

          <div className={styles["toggle-button-wrapper"]}>
            <Button
              {...toggleButtonProps}
              {...(variant === "select" ? { tabIndex: -1, inert: "true" } : {})}
              className={styles["toggle-button"]}
              aria-label="toggle menu"
              icon={Chevron_Right}
              appearance="ghost"
              size="small"
              disabled={disabled}
            />
          </div>
        </div>

        <div className={styles["prefix-wrapper"]}>
          {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}
        </div>

        <div
          className={cx(styles["rows-wrapper"], {
            [styles["max-rows"]]: maxRows !== undefined,
            [styles["single-row"]]: maxRows === 1 && !stillCalculating,
          })}
          ref={rowsRef}
        >
          {selectedItems.length && removeSelectedItem != null
            ? selectedItems
                .slice(0, selectedItemsDisplayCount)
                .map((item, index) => {
                  return (
                    <div
                      key={`selected-item-${index}`}
                      className={styles["chip-wrapper"]}
                    >
                      <Chip
                        {...mergeChipProps(
                          chipProps(item, index),
                          selectedItemProps(item, index),
                          {
                            label: itemToString(item),
                            onClose:
                              disabled || readOnly
                                ? undefined
                                : (_e) => {
                                    removeSelectedItem(item);
                                  },
                            className: styles["chip"],
                            title: itemToString(item),
                          },
                        )}
                      />
                    </div>
                  );
                })
            : null}

          {collapsedChips && maxRows != null ? (
            <div className={styles["chip-wrapper"]}>
              <Chip
                label={`+${selectedItems.length - selectedItemsDisplayCount}`}
                className={styles["chip"]}
                title={selectedItems
                  .slice(selectedItemsDisplayCount)
                  .map((item) => itemToString(item))
                  .join(", ")}
              />
            </div>
          ) : null}

          <Flex className={styles["input-flex"]} alignItems="center">
            {variant === "select" ? (
              <div
                {...inputProps}
                className={cx(ComboboxInputClassNames, inputProps.className)}
              >
                {selectedItems.length ? null : selectedItem ? (
                  itemToString(selectedItem)
                ) : (
                  <span
                    className={styles["fake-placeholder"]}
                    id={placeholderUid}
                  >
                    {placeholder}
                  </span>
                )}
              </div>
            ) : (
              <input
                {...inputProps}
                placeholder={placeholder}
                className={cx(ComboboxInputClassNames, inputProps.className)}
                {...(inputProps["aria-expanded"] != null &&
                inputProps["aria-controls"] != null
                  ? {
                      role: "combobox",
                    }
                  : {})}
              />
            )}

            {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
          </Flex>
        </div>
      </div>

      {hint || errorMessages || warning || description ? (
        <Helper
          id={helperUid}
          error={
            typeof errorMessages === "string" || Array.isArray(errorMessages)
              ? errorMessages
              : undefined
          }
          warning={warning}
          errorMessage={
            isValidElement(errorMessages) ? errorMessages : undefined
          }
          hint={hint}
          description={description}
        />
      ) : null}
    </div>
  );
};
